import React, { useState } from 'react';
import { Tree, Input } from 'antd';
import { DownOutlined } from '@ant-design/icons';

interface DataNode {
  title: string;
  key: string;
  selectable: boolean;
  isLeaf?: boolean;
  children?: DataNode[];
}

const treeData: DataNode[] = [
  {
    title: 'Home',
    key: '0-1-2-2-',
    selectable: false,
    children: [
      { title: 'Help', key: 'help', isLeaf: true, selectable: true },
    ],
  },
  {
    title: 'User Documentation',
    key: '0-1-2-2asd-',
    selectable: false,
    children: [
      {
        title: 'Web-Based Access',
        key: '0-0',
        selectable: false,
        children: [
          { title: 'Selecting Networks, Species, and Adjusting Statistical Parameters', key: 'selectingNetworks', isLeaf: true, selectable: true },
          { title: 'Interacting with the Network', key: 'interacting', isLeaf: true, selectable: true },
          { title: 'Exporting the Network', key: 'exporting', isLeaf: true, selectable: true },
        ],
      },
      {
        title: 'Programmatic Access',
        key: 'programmaticAccess',
        selectable: true,
        children: [],
      },
      {
        title: 'Integration with Cytoscape',
        key: 'integrationWithCytoscape',
        selectable: true,
        children: [],
      },
    ],
  },

];

const Help = () => {
  return (
    <div>
      <div className="text-4xl pb-6 font-thin">
        <span>Help</span>
      </div>
      <p>In this page, you can find several ways of accessing and interacting with GutMicroNet. For the use-case in this Help page, we used the following options:</p>
      <br />
      <ul className="ml-4">
        <li><b>•	Network:</b> Disease</li>
        <li><b>•	Cohort/Disease:</b> NAFLD-NonDiabetic</li>
        <li><b>•	Species Name:</b> “Dorea longicatena 1”; “Ruminococcus bromii 2”</li>
        <li><b>•	Default values for all other options </b></li>
        <li><b>•	Direct Link to the {}
          <a
            href="https://gutmicro.net/?includeNeighbourConnections=true&visualize=true&networkType=Species+Interactions&networkName=Diseases&cohortDiseaseName=NAFLD-NonDiabetic&pruning=0&locations=Species&nodeLimit=10&search=Ruminococcus+bromii+2%7C%2CDorea+longicatena+1&correlation=both&categoryId=18"
            target="_blank"
            rel="noopener noreferrer">
          network</a></b></li>
      </ul>
    </div>
  );
}

const SelectingNetworks = () => {
  return (
    <div>
      <div className="text-4xl pb-6 font-thin">
        <span>Selecting Networks, Species, and Adjusting Statistical Parameters</span>
      </div>
      <p>Network selection can be made by selecting the appropriate network category and cohort/diseases. There are currently 45 networks in GutMicroNet, including overall, geography-, and disease-specific networks. Afterwards, users may choose up to 5 species to be visualized at the same time. Subsequently, users may adjust the statistical parameters, including Edge Significance (-Log10 of FDR), # of Neighbors (Spearman Correlation Ranking), and Correlation Type (Both, Positive-, and Negative-only). This step can also be performed via the search pane in the visualization page.</p>
      <img
        src={process.env.PUBLIC_URL + '/helpFig1.png'}
        alt="Help Figure 1"
        loading="lazy"
        className="pt-4"
      />
    </div>
  );
}

const Interacting = () => {
  return (
    <div>
      <div className="text-4xl pb-6 font-thin">
        <span>Interacting with the Network</span>
      </div>
      <p>Once the network is visualized (by clicking on “Visualize” button in under the search bar), users can start to interact with their networks.</p>
      <br />
      <ul className="ml-4">
        <li><b>•	Getting nodes information:</b> Users can click on a specific node to get more detailed information (e.g. Taxonomic information, KEGG mapping, Region enrichment), including direct link to the Human Microbiome Atlas.</li>
        <li><b>•	Getting edges information:</b> Users can hover through a specific edge to get their correlation score and significance level or click the edge to open a persistent pop up with those information</li>
        <li><b>•	Adding or Removing Nodes:</b> By clicking on a specific node, users can find the remove (the searched nodes) and add (other nodes) to expand or shrink the network</li>
        <li><b>•	Changing the View Options:</b> Users can pick to view the networks as graph or tables (node/edge), change the background, and color group of the nodes (genus by default). The mapping of the colors can be found on the “Legend” section below the “View Options”</li>
      </ul>
      <img
        src={process.env.PUBLIC_URL + '/helpFig2.png'}
        alt="Help Figure 2"
        loading="lazy"
        className="pt-4"
      />
    </div>
  );
}

const Exporting = () => {
  return (
    <div>
      <div className="text-4xl pb-6 font-thin">
        <span>Exporting the Network</span>
      </div>
      <p>Visualized Networks can be downloaded as PNG (including the legend). Moreover, the nodes and edges information can be downloaded as text file (including the color information).</p>
    </div>
  );
}

const ProgrammaticAccess = () => {
  return (
    <div>
      <div className="text-4xl pb-6 font-thin">
        <span>Programmatic Access</span>
      </div>
      <p>API for GutMicroNet can be found in this LINK, including a Jupyter Notebook example on how to use the API and integrate directly the network to iGraph Python.</p>
    </div>
  );
}

const IntegrationWithCytoscape = () => {
  return (
    <div>
      <div className="text-4xl pb-6 font-thin">
        <span>Integration with Cytoscape</span>
      </div>
      <p>Users can directly integrate the exported network tables (See “Web-Based Access” or “Programmatic Access” section on how to export the tables). Below are several quick steps to replicate the network visualization of GutMicroNet in Cytoscape:</p>
      <br />
      <ol className="ml-4">
        <li>1.	Import the edges.txt file (A1)</li>
        <li>2.	Import the nodes.txt file (A2)</li>
        <li>3.	Change the “Fill Color” optuon from Style &gt; Node tab. Select the center box, change the “columns” to “color” and “Mapping Type” to “Passthrough Mapping” (B)</li>
        <li>4.	Change the “Stroke Color (unselected)” option from Style &gt; Edge to color the link/edges depending on the correlation score or other values.</li>
        <li>5.	The network is ready to be presented! (C) Networks can be adjusted and moved as per users’ preference.</li>
        <li>6.	The ready network can be saved as PDF or PNG by selecting “File” &gt; “Export” &gt; “Network to Image” &gt; Select the options</li>
      </ol>
      <br />
      <p>Cytoscape has amazing range of features that users can use, please refer to their {' '}
        <a
          href="https://www.microbiomeatlas.org/"
          target="_blank"
          rel="noopener noreferrer"
        >
          manual
        </a> for more information or guidance.</p>
      <img
        src={process.env.PUBLIC_URL + '/helpFig3.png'}
        alt="Help Figure 3"
        loading="lazy"
        className="pt-4"
      />
    </div>
  );
}



const HelpPage = () => {

  const [helpContent, setHelpContent] = useState("help");

  const onSelect = (keys: React.Key[]) => {
    if (keys.length !== 0) {
      setHelpContent(keys[0].toString())
    }
  }

  return (
    <div className="bg-white h-screen overflow-auto">
      <div className="max-w-5xl px-10 mx-auto my-10 flex flex-row">
        <div className="w-4/12 font-light">
          <div className="text-4xl pb-6 font-thin">
            <span>Content</span>
          </div >
          <Tree defaultExpandAll defaultSelectedKeys={["help"]} showLine={{ showLeafIcon: false }} onSelect={onSelect} icon={null} showIcon={false} switcherIcon={<DownOutlined />} treeData={treeData} />
        </div>
        <div className="pl-8 w-8/12 font-light">
          {(helpContent === "help") && <Help />}
          {(helpContent === "selectingNetworks") && <SelectingNetworks />}
          {(helpContent === "interacting") && <Interacting />}
          {(helpContent === "exporting") && <Exporting />}
          {(helpContent === "programmaticAccess") && <ProgrammaticAccess />}
          {(helpContent === "integrationWithCytoscape") && <IntegrationWithCytoscape />}
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
